/**
 * Landing scoring: the thing four of the five aeroplanes had nothing to do.
 *
 * ## Why this, and why it is not a "course"
 *
 * `armed: config.weapons !== undefined` matches one aircraft, so the trainer, the aerobat, the
 * warbird and the business jet were each offered exactly one mode and two menu rows subtitled
 * "needs an armed aircraft". Meanwhile the simulator already models the one manoeuvre every pilot
 * repeats and every pilot argues about, at any of the baked airports, and threw the result away.
 *
 * A landing is not a gunnery course and is deliberately not stored as one: `CourseId` is a
 * two-value union under `skysim.gunnery.records`, and a landing has no completion time and no
 * ammunition. It keeps its own book, keyed by runway, in `landingRecords.ts`.
 *
 * ## Runway-relative, so this file contains no geodesy
 *
 * Everything is expressed in the runway's own frame — along-track from the threshold, cross-track
 * from the centreline, height above the threshold. The caller does the projection once, and this
 * module is then a pure function of numbers that a test can drive without a world, an airport
 * database or a renderer. That is the same split `fdm.ts` uses against `geo.ts`, and it is why
 * every case below is testable: a 3° approach, a duck-under, a float, a firm arrival on the
 * numbers, and a runway you touched down beyond the far end of.
 *
 * ## What is scored, and why each one
 *
 * - **Glideslope** and **centreline** tracking on final, as RMS deviation. Not the worst excursion:
 *   one gust should not score the same as flying the whole approach a dot low.
 * - **Threshold crossing height**. The single number an examiner watches, because it is the one
 *   that decides whether the rest of the landing is possible.
 * - **Touchdown point**, against the aiming point. Long is more dangerous than short here, and is
 *   penalised harder — running out of runway is a different class of outcome from a firm arrival.
 * - **Sink rate at touchdown**. What separates a landing from an arrival.
 * - **Stopping distance**, because a landing is not over at touchdown.
 *
 * `updateFixed` / `updateFrame`: {@link LandingScorer.sample} is called on the fixed step, which is
 * where the flight state it reads is computed. Allocation-free; the result object is built once, on
 * the transition to stopped.
 */

import { metresPerDegreeLatitude, metresPerDegreeLongitude } from '../../world/geo.js';

/**
 * Project a geodetic position into a runway's own frame.
 *
 * Small-angle flat-earth about the landing threshold, and that is not a compromise: an approach
 * happens inside about 8 km of one point, where the error from ignoring curvature is centimetres,
 * and the alternative is dragging ECEF and a full ENU basis into a scoring module for no gain.
 * `metresPerDegreeLatitude`/`Longitude` already carry the WGS84 ellipsoid's own scale factors, so
 * this is not a spherical approximation either — it is the ellipsoid, linearised where linearising
 * it is exact to the precision anyone can fly.
 */
export function projectToRunway(
  runway: RunwayEnds,
  position: { latitudeDeg: number; longitudeDeg: number; altitudeM: number },
): { alongM: number; crossM: number; heightM: number } {
  const perLat = metresPerDegreeLatitude(runway.thresholdLatitudeDeg);
  const perLon = metresPerDegreeLongitude(runway.thresholdLatitudeDeg);
  // East/north of the landing threshold.
  const eastM = (position.longitudeDeg - runway.thresholdLongitudeDeg) * perLon;
  const northM = (position.latitudeDeg - runway.thresholdLatitudeDeg) * perLat;
  // The runway's own axis. True heading is clockwise from north, so this is the usual swap.
  const headingRad = (runway.headingDegTrue * Math.PI) / 180;
  const alongUnitE = Math.sin(headingRad);
  const alongUnitN = Math.cos(headingRad);
  return {
    alongM: eastM * alongUnitE + northM * alongUnitN,
    // Right of the centreline is the along-axis rotated 90° clockwise.
    crossM: eastM * alongUnitN - northM * alongUnitE,
    heightM: position.altitudeM - runway.thresholdElevationM,
  };
}

/** The landing threshold and the direction of landing. */
export interface RunwayEnds {
  thresholdLatitudeDeg: number;
  thresholdLongitudeDeg: number;
  thresholdElevationM: number;
  /** True heading of the landing direction [deg]. */
  headingDegTrue: number;
}

/** Standard approach angle [deg]. Every ILS in the world, and what a visual approach imitates. */
export const STANDARD_GLIDESLOPE_DEG = 3;

/** Aiming point on a normal runway [m from threshold]. The 300 m markings. */
export const AIMING_POINT_M = 300;

/** Height below which the approach is being scored [m]. Roughly a mile and a half out on 3°. */
export const FINAL_GATE_HEIGHT_M = 120;

/** Ground speed below which the aeroplane counts as stopped [m/s]. */
export const STOPPED_SPEED_MPS = 1.5;

/** The runway being landed on, in its own terms. */
export interface RunwayFrame {
  lengthM: number;
  widthM: number;
  /** Target approach angle [deg]. Defaults to {@link STANDARD_GLIDESLOPE_DEG}. */
  glideslopeDeg?: number;
  /** Aiming point from the threshold [m]. Defaults to {@link AIMING_POINT_M}. */
  aimingPointM?: number;
}

/** One tick of flight, in the runway's frame. */
export interface LandingSample {
  /** Along the runway from the threshold. Negative on approach, positive past it [m]. */
  alongM: number;
  /** Signed distance right of the centreline [m]. */
  crossM: number;
  /** Height above the threshold [m]. */
  heightM: number;
  groundSpeedMps: number;
  onGround: boolean;
  /** Rate of descent, positive downward [m/s]. */
  sinkRateMps: number;
}

export interface LandingResult {
  /** Height crossing the threshold [m]. Null if the aeroplane never crossed it airborne. */
  thresholdHeightM: number | null;
  /** Where the wheels first touched, from the threshold [m]. Negative means short of it. */
  touchdownAlongM: number;
  /** Offset from the centreline at touchdown [m]. */
  touchdownCrossM: number;
  /** Sink rate at touchdown [m/s]. */
  touchdownSinkRateMps: number;
  /** Roll-out from touchdown to stopped [m]. Null if it never stopped on the runway. */
  stoppingDistanceM: number | null;
  /** RMS glideslope error over the scored final [deg]. */
  glideslopeRmsDeg: number;
  /** RMS centreline error over the scored final [m]. */
  centrelineRmsM: number;
  /** Did the aeroplane stop with runway remaining? */
  stoppedOnRunway: boolean;
  /** 0–100. See {@link scoreLanding}. */
  score: number;
  grade: LandingGrade;
}

export type LandingGrade = 'greaser' | 'good' | 'firm' | 'rough' | 'go around';

/**
 * Score a completed landing, 0–100.
 *
 * Each term is a penalty measured against a tolerance a pilot would recognise, then the total is
 * subtracted from 100. Deliberately not a weighted average of normalised scores: an approach that
 * is perfect in five respects and lands 900 m long is not an 83 % landing, and the additive form
 * lets a single serious error dominate the way it should.
 */
export function scoreLanding(result: Omit<LandingResult, 'score' | 'grade'>): number {
  let penalty = 0;

  // Sink rate. 0.5 m/s is a greaser, 1.8 firm, 3+ is structural.
  const sink = Math.max(0, result.touchdownSinkRateMps);
  penalty += sink <= 0.5 ? 0 : ((sink - 0.5) / 1.3) * 18;

  /*
   * Touchdown point. Long is penalised at twice the rate of short, because the two failures are
   * not symmetrical: short of the threshold is embarrassing, long is how runway excursions happen.
   */
  const aim = result.touchdownAlongM;
  const target = AIMING_POINT_M;
  const longBy = aim - target;
  penalty += longBy >= 0 ? (longBy / 300) * 20 : (-longBy / 300) * 10;

  // Centreline at touchdown, against a half-width of about 22 m on a normal runway.
  penalty += (Math.abs(result.touchdownCrossM) / 10) * 8;

  // Tracking on final.
  penalty += (result.glideslopeRmsDeg / 1.0) * 12;
  penalty += (result.centrelineRmsM / 20) * 8;

  // Threshold crossing height: 15 m is the target, and low is worse than high.
  if (result.thresholdHeightM !== null) {
    const error = result.thresholdHeightM - 15;
    penalty += error >= 0 ? (error / 15) * 6 : (-error / 15) * 12;
  }

  // Failing to stop on the runway is not a landing, whatever the touchdown was like.
  if (!result.stoppedOnRunway) penalty += 35;

  return Math.max(0, Math.min(100, Math.round(100 - penalty)));
}

/** The grade a score earns. Named the way pilots talk, not A–F. */
export function gradeFor(score: number, sinkRateMps: number): LandingGrade {
  if (score < 40) return 'go around';
  if (sinkRateMps > 2.5) return 'rough';
  if (score >= 90 && sinkRateMps <= 0.6) return 'greaser';
  if (score >= 75) return 'good';
  return 'firm';
}

/**
 * Watches an approach and produces one {@link LandingResult} per landing.
 *
 * Fed every fixed step. It is a small state machine — approaching, rolling, stopped — and it only
 * ever reports once per touchdown, so a bounce does not produce a second, better-looking result
 * than the arrival that caused it.
 */
export class LandingScorer {
  private readonly runway: RunwayFrame;
  private readonly glideslopeDeg: number;
  private readonly aimingPointM: number;

  private glideslopeSumSq = 0;
  private centrelineSumSq = 0;
  private samples = 0;
  private thresholdHeightM: number | null = null;
  private previousAlongM: number | null = null;
  private previousHeightM = 0;
  private previousAirborne = true;
  private lastAirborneSinkRateMps = 0;

  private touchdown: { alongM: number; crossM: number; sinkRateMps: number } | null = null;
  private result: LandingResult | null = null;

  constructor(runway: RunwayFrame) {
    this.runway = runway;
    this.glideslopeDeg = runway.glideslopeDeg ?? STANDARD_GLIDESLOPE_DEG;
    this.aimingPointM = runway.aimingPointM ?? AIMING_POINT_M;
  }

  /** The finished landing, or null while one is still in progress. */
  get completed(): LandingResult | null {
    return this.result;
  }

  /** Has the aeroplane touched down yet? */
  get hasTouchedDown(): boolean {
    return this.touchdown !== null;
  }

  /** Feed one fixed step. */
  sample(state: LandingSample): void {
    if (this.result !== null) return;

    if (!state.onGround && this.touchdown === null) {
      this.trackApproach(state);
      this.captureThresholdCrossing(state);
    }

    // Touchdown: the first ground contact of this approach.
    if (state.onGround && this.previousAirborne && this.touchdown === null) {
      this.touchdown = {
        alongM: state.alongM,
        crossM: state.crossM,
        // The sample that first reports ground contact has already been arrested by the gear
        // spring, so the descent rate carried into the flare is the previous step's.
        sinkRateMps: this.lastAirborneSinkRateMps,
      };
    }

    if (this.touchdown !== null && state.onGround && state.groundSpeedMps <= STOPPED_SPEED_MPS) {
      this.finish(state);
    }

    if (!state.onGround) this.lastAirborneSinkRateMps = Math.max(0, state.sinkRateMps);
    this.previousAirborne = !state.onGround;
    this.previousAlongM = state.alongM;
    this.previousHeightM = state.heightM;
  }

  /** RMS tracking, over the part of the approach that is actually an approach. */
  private trackApproach(state: LandingSample): void {
    if (state.alongM > 0 || state.heightM > FINAL_GATE_HEIGHT_M || state.heightM <= 0) return;
    /*
     * Range to the **aiming point**, not to the threshold.
     *
     * A glideslope is referenced to the point it puts you on, and measuring to the threshold makes
     * the angle diverge as you reach it: a perfectly flown 3° approach measured that way reads
     * 3.4° two kilometres out and 58° ten metres short of the numbers, for an RMS error of 6.98°
     * on an approach with no error in it at all.
     */
    const rangeM = this.aimingPointM - state.alongM;
    if (rangeM < 1) return;
    const actualDeg = Math.atan2(state.heightM, rangeM) * (180 / Math.PI);
    this.glideslopeSumSq += (actualDeg - this.glideslopeDeg) ** 2;
    this.centrelineSumSq += state.crossM ** 2;
    this.samples += 1;
  }

  /** Height at the moment the threshold went under the wheels, by interpolation. */
  private captureThresholdCrossing(state: LandingSample): void {
    if (this.thresholdHeightM !== null || this.previousAlongM === null) return;
    if (this.previousAlongM >= 0 || state.alongM < 0) return;
    // Linear between the two straddling samples: at 120 kt a fixed step is under a metre, so a
    // straight line through them is well inside the precision anyone cares about.
    const span = state.alongM - this.previousAlongM;
    const fraction = span === 0 ? 0 : -this.previousAlongM / span;
    this.thresholdHeightM =
      this.previousHeightM + (state.heightM - this.previousHeightM) * fraction;
  }

  private finish(state: LandingSample): void {
    const touchdown = this.touchdown;
    if (touchdown === null) return;
    const stoppingDistanceM = state.alongM - touchdown.alongM;
    const partial = {
      thresholdHeightM: this.thresholdHeightM,
      touchdownAlongM: touchdown.alongM,
      touchdownCrossM: touchdown.crossM,
      touchdownSinkRateMps: touchdown.sinkRateMps,
      stoppingDistanceM,
      glideslopeRmsDeg: this.samples === 0 ? 0 : Math.sqrt(this.glideslopeSumSq / this.samples),
      centrelineRmsM: this.samples === 0 ? 0 : Math.sqrt(this.centrelineSumSq / this.samples),
      stoppedOnRunway: state.alongM <= this.runway.lengthM && Math.abs(state.crossM) <= this.runway.widthM,
    };
    const score = scoreLanding(partial);
    this.result = { ...partial, score, grade: gradeFor(score, touchdown.sinkRateMps) };
  }
}
